import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import componentStyles from './fil-link.styles.js';

/**
 * @tag fil-link
 * @property {"black"|"orange"|"white"|"black-orange"} [color] Color of the component
 * @property {boolean} [disabled=false] Disabled state of the component
 * @property {string} [download] File name in case of download in the component
 * @property {boolean} [hideUnderline] Attribute to show/hide underline in the component
 * @property {string} [href] URL of the component
 * @property {string} [hrefLang] HTML hrefLang attribute of the component
 * @property {string} [linkRole] HTML linkRole attribute of the component
 * @property {string} [ping] HTML ping attribute of the component
 * @property {string} [rel] HTML rel attribute of the component
 * @property {"standard"|"small"} [size] Size of the component
 * @property {string} [target="_blank"] HTML target attribute of the component
 * @property {string} [type] MIME type of the target of the component
 * @property {string} [id]
 * @property {string} [label] An accessible label that describes the component. It will be applied to aria-label, but not visually rendered.
 * @property {string} [labelledby]
 */
export class FilLink extends LitElement {
  static properties = {
    color: { type: String }, // 'black' | 'orange' | 'white' | 'black-orange'
    disabled: { type: Boolean },
    download: { type: String, reflect: true },
    hideUnderline: { type: Boolean },
    href: { type: String, reflect: true },
    hreflang: { type: String, reflect: true },
    linkRole: { type: String },
    ping: { type: String, reflect: true },
    rel: { type: String, reflect: true },
    size: { type: String }, // 'large' | 'small' | 'standard'
    target: { type: String, reflect: true },
    type: { type: String, reflect: true },
    id: { type: String },
    label: { type: String }, //
    labelledby: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.disabled = false;
    this.size = 'standard';
    this.label = 'link';
  }

  /**
   * The CSS class list for the link node.
   */
  get classes() {
    const { size, color } = this;

    return classMap({
      'fil-link--disabled': this.disabled,
      [`fil-link--${size}`]: size !== 'standard',
      'fil-link--no-underline': this.hideUnderline,
      [`fil-link--${color}`]: color,
    });
  }

  /**
   * @returns The disabled link content.
   */
  renderLinkDisabled() {
    return html`<span class="fil-link${this.classes}"><slot></slot></span>`;
  }

  /**
   * @returns The link content.
   */
  renderLink() {
    const {
      id,
      classes,
      download,
      href,
      hreflang,
      linkRole,
      ping,
      rel,
      target,
      type,
      label,
      labelledby,
    } = this;

    return html`
      <a
        id=${id}
        class="fil-link${classes}"
        download="${ifDefined(download)}"
        href="${ifDefined(href)}"
        hreflang="${ifDefined(hreflang)}"
        title="${ifDefined(label)}"
        aria-label=${ifDefined(label)}
        aria-labelledby=${ifDefined(labelledby)}
        ping="${ifDefined(ping)}"
        rel="${ifDefined(rel)}"
        role="${ifDefined(linkRole)}"
        target="${ifDefined(target)}"
        type="${ifDefined(type)}"
      >
        <slot></slot>
      </a>
    `;
  }

  render() {
    return this.disabled ? this.renderLinkDisabled() : this.renderLink();
  }
}

export default FilLink;
